import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { flattenPages } from '@/api/pagination'
import type { RoomVisitDto } from '@/api/types'
import {
  useDeleteWorld,
  useFeatureWorld,
  useRestoreWorld,
  worldDetailQuery,
  worldInstancesQuery,
  worldVisitsQuery,
  worldPrivacyLabel,
  worldStatusLabel,
} from '@/features/worlds/worlds.queries'
import { WorldEditDialog } from '@/features/worlds/WorldEditDialog'
import { PERMISSIONS, usePermission } from '@/features/auth/permissions'
import { formatBytes, formatDate, formatNumber, formatRelative } from '@/lib/format'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  Copyable,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatCard,
  TabPanel,
  Tabs,
  useToast,
} from '@/ui'
import { IconArrowBack, IconDelete, IconEdit, IconPin, IconStar, IconUndo } from '@/icons'

const TABS = [
  { id: 'overview', labelKey: 'worlds.tabOverview' },
  { id: 'instances', labelKey: 'worlds.tabInstances' },
  { id: 'visits', labelKey: 'worlds.tabVisits' },
] as const

type TabId = (typeof TABS)[number]['id']
const TAB_IDS = TABS.map((tab) => tab.id) as unknown as [TabId, ...TabId[]]

const searchSchema = z.object({ tab: z.enum(TAB_IDS).default('overview') })

export const Route = createFileRoute('/_authed/worlds/$slug')({
  validateSearch: searchSchema,
  component: WorldDetailPage,
})

function WorldDetailPage() {
  const { t, i18n } = useTranslation()
  const { slug } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const toast = useToast()

  const canFeature = usePermission(PERMISSIONS.WORLD_FEATURE)
  const canDelete = usePermission(PERMISSIONS.WORLD_DELETE)
  const canUpdate = usePermission(PERMISSIONS.WORLD_UPDATE)

  const query = useQuery(worldDetailQuery(slug))
  const instances = useQuery({ ...worldInstancesQuery(slug), enabled: search.tab === 'instances' })
  const visits = useInfiniteQuery({ ...worldVisitsQuery(slug), enabled: search.tab === 'visits' })

  const feature = useFeatureWorld()
  const remove = useDeleteWorld()
  const restore = useRestoreWorld()

  const [confirming, setConfirming] = useState<'delete' | 'restore' | null>(null)
  const [isEditing, setIsEditing] = useState(false)

  if (query.isPending) return <LoadingState />
  if (query.isError) {
    return (
      <ErrorState
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
      />
    )
  }

  const world = query.data
  const { items: visitRows } = flattenPages<RoomVisitDto>(visits.data)

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link
            to="/worlds"
            search={{ q: '', status: 'all', privacy: 'all', sort: 'createdAt', order: 'desc' }}
            className="mb-2 inline-flex items-center gap-1 text-[0.8125rem] text-text-secondary hover:text-text"
          >
            <IconArrowBack size={14} />
            {t('worlds.title')}
          </Link>
        }
        title={world.title}
        description={world.ownerDisplayName}
        actions={
          <>
            {canUpdate ? (
              <Button
                variant="secondary"
                icon={<IconEdit size={15} />}
                onClick={() => {
                  setIsEditing(true)
                }}
              >
                {t('common.edit')}
              </Button>
            ) : null}

            {canFeature ? (
              <Button
                variant={world.isFeatured ? 'secondary' : 'primary'}
                icon={<IconPin size={15} />}
                isLoading={feature.isPending}
                onClick={() => {
                  feature.mutate(
                    { slug, isFeatured: !world.isFeatured },
                    {
                      onSuccess: () => {
                        toast.success(t('worlds.featureSaved'))
                      },
                      onError: (error) => {
                        toast.fromError(error, t('state.errorTitle'))
                      },
                    },
                  )
                }}
              >
                {world.isFeatured ? t('worlds.unfeature') : t('worlds.feature')}
              </Button>
            ) : null}

            {canDelete ? (
              world.isDeleted ? (
                <Button
                  variant="secondary"
                  icon={<IconUndo size={15} />}
                  onClick={() => {
                    setConfirming('restore')
                  }}
                >
                  {t('worlds.restore')}
                </Button>
              ) : (
                <Button
                  variant="danger"
                  icon={<IconDelete size={15} />}
                  onClick={() => {
                    setConfirming('delete')
                  }}
                >
                  {t('worlds.deleteWorld')}
                </Button>
              )
            ) : null}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {world.isDeleted ? (
          <Badge tone="danger" hasDot>
            {t('worlds.badgeDeleted')}
          </Badge>
        ) : (
          <Badge tone={world.status === 'PUBLISHED' ? 'success' : 'neutral'} hasDot>
            {((key) => (key ? t(key) : world.status))(worldStatusLabel(world.status))}
          </Badge>
        )}
        <Badge>
          {((key) => (key ? t(key) : world.privacy))(worldPrivacyLabel(world.privacy))}
        </Badge>
        {world.isFeatured ? <Badge tone="primary">{t('worlds.badgeFeatured')}</Badge> : null}
        <Copyable value={world.slug} />
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label={t('worlds.columnActive')}
          value={formatNumber(world.activeCount, i18n.language)}
        />
        <StatCard
          label={t('worlds.columnVisits')}
          value={formatNumber(world.visits, i18n.language)}
          sublabel={`${formatNumber(world.uniqueVisitors, i18n.language)} ${t('worlds.uniqueVisitors').toLowerCase()}`}
        />
        <StatCard
          label={t('worlds.sceneObjects')}
          value={formatNumber(world.sceneObjectCount, i18n.language)}
        />
        <StatCard
          label={t('overview.storageUsed')}
          value={formatBytes(world.storage.placedAssetBytes, i18n.language)}
          sublabel={`${formatNumber(world.storage.placedAssetCount, i18n.language)} ${t('worlds.sceneObjects').toLowerCase()}`}
        />
      </div>

      <Tabs
        tabs={TABS.map((tab) => ({ id: tab.id, label: t(tab.labelKey) }))}
        activeId={search.tab}
        onChange={(tab) => {
          void navigate({ search: { tab: tab as TabId } })
        }}
        className="mb-4"
      />

      <TabPanel id={search.tab}>
        {search.tab === 'overview' ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title={t('worlds.detailTitle')} as="h3" />
              <dl className="mt-3 flex flex-col gap-2 text-[0.8125rem]">
                <Row label={t('worlds.fieldDescription')} value={world.description ?? '—'} />
                <Row label={t('worlds.fieldCategory')} value={world.categoryKey ?? '—'} />
                <Row
                  label={t('worlds.columnCreated')}
                  value={formatDate(world.createdAt, i18n.language)}
                />
                <Row label={t('worlds.instances')} value={String(world.instanceCount)} />
              </dl>
            </Card>

            <Card>
              <CardHeader title={t('worlds.ratingBreakdown')} as="h3" />
              {world.ratingCount === 0 ? (
                <p className="mt-3 text-[0.8125rem] text-text-secondary">{t('worlds.noRatings')}</p>
              ) : (
                <>
                  <p className="mt-2 flex items-center gap-1.5 text-lg font-semibold text-text">
                    <IconStar size={16} className="text-warning" />
                    {world.averageRating?.toFixed(1) ?? '—'}
                    <span className="text-[0.8125rem] font-normal text-text-secondary">
                      ({formatNumber(world.ratingCount, i18n.language)})
                    </span>
                  </p>
                  <ul className="mt-3 flex flex-col gap-1.5">
                    {[...world.ratingBreakdown]
                      .sort((a, b) => b.value - a.value)
                      .map((bucket) => (
                        <li key={bucket.value} className="flex items-center gap-2 text-xs">
                          <span data-numeric className="w-3 text-text-secondary">
                            {bucket.value}
                          </span>
                          <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-sunken">
                            <span
                              className="block h-full bg-warning"
                              style={{
                                width: `${String((bucket.count / Math.max(world.ratingCount, 1)) * 100)}%`,
                              }}
                            />
                          </span>
                          <span data-numeric className="w-8 text-right text-text-secondary">
                            {bucket.count}
                          </span>
                        </li>
                      ))}
                  </ul>
                </>
              )}
            </Card>
          </div>
        ) : null}

        {search.tab === 'instances' ? (
          <Card isFlush>
            {instances.isPending ? (
              <LoadingState />
            ) : instances.data && instances.data.items.length > 0 ? (
              <ul className="divide-y divide-border">
                {instances.data.items.map((instance) => (
                  <li key={instance.id} className="flex items-center gap-3 px-5 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[0.8125rem] font-medium text-text">
                        {instance.ownerDisplayName}
                      </p>
                      <p className="truncate font-mono text-xs text-text-secondary">
                        {instance.joinCode}
                      </p>
                    </div>
                    <span data-numeric className="text-[0.8125rem] text-text-secondary">
                      {formatNumber(instance.visits, i18n.language)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title={t('worlds.noInstances')} />
            )}
          </Card>
        ) : null}

        {search.tab === 'visits' ? (
          <Card isFlush>
            {visits.isPending ? (
              <LoadingState />
            ) : visitRows.length > 0 ? (
              <>
                <ul className="divide-y divide-border">
                  {visitRows.map((visit) => (
                    <li key={`${visit.userId}-${visit.visitedAt}`} className="flex items-center gap-3 px-5 py-3">
                      <Link
                        to="/users/$userId"
                        params={{ userId: visit.userId }}
                        search={{ tab: 'overview' }}
                        className="min-w-0 flex-1 truncate text-[0.8125rem] font-medium text-text hover:underline"
                      >
                        {visit.displayName}
                      </Link>
                      <span className="shrink-0 text-xs text-text-secondary">
                        {formatRelative(visit.visitedAt, i18n.language)}
                      </span>
                    </li>
                  ))}
                </ul>
                {visits.hasNextPage ? (
                  <div className="flex justify-center border-t border-border p-3">
                    <Button
                      variant="secondary"
                      size="sm"
                      isLoading={visits.isFetchingNextPage}
                      onClick={() => {
                        void visits.fetchNextPage()
                      }}
                    >
                      {t('common.loadMore')}
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <EmptyState title={t('worlds.noVisits')} />
            )}
          </Card>
        ) : null}
      </TabPanel>

      <WorldEditDialog
        key={isEditing ? world.slug : "none"}
        world={isEditing ? world : null}
        onClose={() => {
          setIsEditing(false)
        }}
      />

      <ConfirmDialog
        isOpen={confirming === 'delete'}
        onClose={() => {
          setConfirming(null)
        }}
        onConfirm={() => {
          remove.mutate(
            { slug },
            {
              onSuccess: () => {
                toast.success(t('worlds.deleted'))
                setConfirming(null)
              },
              onError: (error) => {
                toast.fromError(error, t('state.errorTitle'))
              },
            },
          )
        }}
        title={t('worlds.deleteConfirm')}
        body={t('worlds.deleteBody', { title: world.title })}
        confirmLabel={t('worlds.deleteWorld')}
        cancelLabel={t('common.cancel')}
        isPending={remove.isPending}
      />

      <ConfirmDialog
        isOpen={confirming === 'restore'}
        onClose={() => {
          setConfirming(null)
        }}
        onConfirm={() => {
          restore.mutate(
            { slug },
            {
              onSuccess: () => {
                toast.success(t('worlds.restored'))
                setConfirming(null)
              },
              onError: (error) => {
                toast.fromError(error, t('state.errorTitle'))
              },
            },
          )
        }}
        title={t('worlds.restoreConfirm')}
        body={t('worlds.restoreBody', { title: world.title })}
        confirmLabel={t('worlds.restore')}
        cancelLabel={t('common.cancel')}
        isDestructive={false}
        isPending={restore.isPending}
      />
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-text-secondary">{label}</dt>
      <dd className="min-w-0 truncate text-right text-text">{value}</dd>
    </div>
  )
}
